import {
  asLocale,
  configureApiClient,
  getLocale,
  getOrCreateDeviceUuid,
  intlLocale,
} from '@saniplus/api-client';
import type { Locale } from '@saniplus/contracts';
import type { i18n as I18n } from 'i18next';

import { API_BASE_URL, IDB_NAME } from './config';
import { store } from './store';
import { persistServerTime, rehydrateQueue, startOfflineScheduler } from '../features/offline';
import { initI18n } from '../i18n';
import { registerServiceWorker } from '../pwa/serviceWorker';

export interface BootstrapResult {
  readonly i18n: I18n;
  readonly locale: Locale;
  readonly deviceUuid: string;
}

/**
 * Everything that must be true before the first paint, in the order it must
 * be true in.
 *
 * The order is the whole point and it is asserted by `bootstrap.spec.ts`:
 *
 * 1. **`getOrCreateDeviceUuid` first, and awaited.** It is what runs the
 *    IndexedDB upgrade that creates every store — `meta` *and* the four the
 *    punch queue lives in. `features/offline/db.ts` opens the same database at
 *    a pinned version; if the queue got there first it would race the upgrade,
 *    and a login without a device uuid can only ever be refused (D-06).
 * 2. The locale, because a screen rendered in the wrong language and then
 *    repainted is worse than a hundred milliseconds of blank.
 * 3. `rehydrateQueue`, because a shell that paints "tout est synchronisé"
 *    before reading the queue has told a worker their punch went through when
 *    it did not.
 * 4. The scheduler, which may flush immediately — and must not flush a queue
 *    nobody has read yet.
 *
 * It is a separate module from `main.tsx` so the order can be tested. A
 * top-level `void bootstrap()` in an entry file is unimportable.
 */
export async function bootstrapApp(): Promise<BootstrapResult> {
  const deviceUuid = await getOrCreateDeviceUuid(IDB_NAME);

  // Client-side half of the amendment #20 precedence: stored choice, then the
  // browser's language, then French.
  let locale: Locale = (await getLocale(IDB_NAME)) ?? asLocale(navigator.language) ?? 'fr';

  configureApiClient({
    baseUrl: API_BASE_URL,
    getLocale: () => locale,
    getDeviceUuid: () => deviceUuid,
    getUserAgent: () => navigator.userAgent,
    /*
     * `persistServerTime`, not the api-client's bare `recordServerTime`: it
     * records the anchor AND writes it to IndexedDB, so a cold start after a
     * phone has been off all night still knows the offset between this
     * device's clock and the server's (D-10). Elapsed time on the punch screen
     * is measured from the server's instant, never the phone's.
     */
    onServerTime: persistServerTime,
  });

  const i18n = initI18n(locale);
  document.documentElement.lang = intlLocale(locale);
  // The worker PWA is `night` whatever the phone's OS says: a worker whose
  // phone is in light mode is still standing in a dark plant.
  document.documentElement.dataset['theme'] = 'night';

  /*
   * `useLocale` (api-client) owns a language change itself: it flips i18next,
   * updates the `?lang=` closure, persists `meta.locale` and posts
   * `/me/locale`. What is left here is the one thing a hook cannot do — keep
   * the document's own `lang` attribute honest, which is what a screen reader
   * switches voice on.
   */
  i18n.on('languageChanged', (next: string) => {
    locale = asLocale(next) ?? 'fr';
    document.documentElement.lang = intlLocale(locale);
  });

  // Restores the clock anchor and the queue, and un-sticks any row a killed
  // process left `sending`. At this point nobody is signed in yet — the queue
  // is owner-scoped, so this pass can only ever clear the mirror. It runs
  // anyway, for the ordering above and for the `online` flag; the real read is
  // `rehydrateOnSignIn` below, once there is an owner to scope it to.
  await store.dispatch(rehydrateQueue());

  rehydrateOnSignIn();

  startOfflineScheduler(store.dispatch, {
    hasPending: () => store.getState().offlineQueue.pending.length > 0,
  });

  /*
   * Last, and unconditionally.
   *
   * It is last because a registration that starts precaching must not compete
   * with the device uuid's IndexedDB upgrade or the queue's first read. It is
   * unconditional because everything the PWA promises depends on it: the
   * offline shell, the navigation fallback and
   * `beforeinstallprompt`, which Chrome only fires once a service worker
   * controls `start_url`. Registering it from a screen (it used to live in
   * `UpdatePrompt`, on Profil alone) meant no install offer and no offline
   * shell for a worker who never opened the settings tab.
   */
  registerServiceWorker();

  return { i18n, locale, deviceUuid };
}

/**
 * Re-read the queue every time the signed-in user changes to somebody.
 *
 * `rehydrateQueue` is scoped to `auth.user.id` and refuses to guess: with no
 * session it clears the mirror rather than show one worker another's punches
 * (`features/offline/thunks.ts`). But `bootstrapApp` runs *before* the store
 * has a session — `SessionGate` dispatches `bootstrapSession` from a mount
 * effect, which is necessarily later — so the boot pass always ran as nobody
 * and always cleared. A worker who queued a punch offline and then reloaded
 * (or whose phone reloaded the tab for them) got an empty mirror over a punch
 * still sitting in IndexedDB: the home screen dropped out of `OFFLINE_QUEUED`
 * back to `NOT_PUNCHED_IN_WINDOW_OPEN` and invited them to punch in again.
 *
 * A subscription rather than a listener middleware because the store is built
 * by the shared factory and this is the app's own wiring, not the api-client's.
 * It fires on `bootstrapSession.fulfilled` (reload) and on `login.fulfilled`
 * (a second worker taking the phone) alike, because it watches the identity
 * itself and not the actions that happen to set it.
 */
function rehydrateOnSignIn(): void {
  let lastOwner: string | null = store.getState().auth.user?.id ?? null;

  store.subscribe(() => {
    const owner = store.getState().auth.user?.id ?? null;
    if (owner === lastOwner) return;
    lastOwner = owner;
    // A sign-*out* needs no pass: `signOut` already clears the mirror, and a
    // rehydrate with no owner would only clear it a second time.
    if (owner === null) return;
    void store.dispatch(rehydrateQueue());
  });
}
